import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, aiAPI, settingsAPI } from '../services/api';
import {
  FaFlask, FaBoxOpen, FaCog, FaCheckCircle, FaGraduationCap, FaClipboardList,
  FaSync, FaArrowRight, FaBell, FaChartLine, FaChartBar, FaChartPie,
  FaExclamationTriangle, FaClock, FaTasks, FaFileAlt, FaUsers, FaTachometerAlt, FaRobot,
  FaThLarge, FaExpandAlt, FaFileExcel, FaFilePdf, FaDownload, FaCaretRight, FaEye
} from 'react-icons/fa';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, AreaChart, Area, RadialBarChart, RadialBar
} from 'recharts';
import { StatCard, Button, LoadingSpinner } from '../components/UnifiedUIComponents';
import { Modal } from '../components/Modals';
import { toast } from 'react-toastify';
import { SearchBox, FilterSelect } from '../components/ListToolbar';
import AIResultPanel from '../components/AIResultPanel';
import useAIAction from '../hooks/useAIAction';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { openPrintPreview, getActivePrintUnitName } from '../utils/printUtils';
import TablePagination from '../components/TablePagination';
import { unitReportAddress } from '../utils/reportProfiles';

// Fixed order — mirrors the sign-off trail in Results.js (STAGE_LABELS / STEP_CONFIG).
const FUNNEL_STAGES = [
  { key: 'draft', label: 'Draft' },
  { key: 'cleaned', label: 'Cleaned' },
  { key: 'sampled', label: 'Sampled' },
  { key: 'visual_done', label: 'Visual Done' },
  { key: 'analyzed', label: 'Analyzed' },
  { key: 'reported', label: 'Reported' },
  { key: 'approved', label: 'QA Approved' }
];

const OUTCOME_COLOR = { PASS: 'var(--success-text)', FAIL: 'var(--danger-text)', Pending: 'var(--warning-text)' };
const outcomeColor = (status) => OUTCOME_COLOR[status] || 'var(--info-text)';

// Which filter (if any) a stat tile's "View Full Page" should carry into the
// target list, so drilling into "Approved Results" lands on the Approved card
// instead of the unfiltered list.
const STAT_TILE_FILTERS = {
  approvedResults: { filterStage: 'approved' },
};

const EQUIPMENT_STATUS_COLOR = {
  'Approved': 'var(--success-text)',
  'Pending Approval': 'var(--warning-text)',
  'Draft': 'var(--info-text)',
  'Rejected': 'var(--danger-text)'
};
const CHART_SERIES = [
  'var(--chart-series-1)', 'var(--chart-series-2)', 'var(--chart-series-3)',
  'var(--chart-series-4)', 'var(--chart-series-5)', 'var(--chart-series-6)'
];
const categoricalColor = (idx) => CHART_SERIES[idx % CHART_SERIES.length];
const equipmentColor = (status, idx) => EQUIPMENT_STATUS_COLOR[status] || categoricalColor(idx);

// Single-hue ordinal ramp (draft → approved), stepped off the app's own
// primary color so the funnel follows whichever color theme the user picked.
const funnelFill = (idx, total) => `color-mix(in srgb, var(--primary-color) ${18 + Math.round((idx / Math.max(total - 1, 1)) * 78)}%, var(--card-bg))`;

const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '8px 12px', fontSize: 'var(--fs-sm)', boxShadow: 'var(--shadow-md)' }}>
      {label && <div style={{ fontWeight: 700, marginBottom: '4px', color: 'var(--text-primary)' }}>{label}</div>}
      {payload.map((p, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-secondary)' }}>
          <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: p.color || p.fill }} />
          {formatter ? formatter(p) : `${p.name}: ${p.value}`}
        </div>
      ))}
    </div>
  );
};

const ChartEmptyState = ({ icon: Icon, message }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2.5rem 1rem', color: 'var(--text-secondary)' }}>
    <Icon style={{ fontSize: 'var(--fs-3xl)', marginBottom: '0.75rem', opacity: 0.4 }} />
    <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>{message}</p>
  </div>
);

// Small select used as a card's own filter/sort — LMS <Select size="small">.
const CardControl = ({ value, onChange, options, title }) => (
  <select
    className="form-control form-control-sm !w-auto"
    value={value}
    onChange={onChange}
    title={title}
    onClick={(e) => e.stopPropagation()}
  >
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

// LMS dashboard card: title bar (icon + title, actions on the right) over a
// padded body — same structure as lms-tms-app ui/Card.jsx with title/extra.
const DashCard = ({ icon: Icon, iconColor, title, badge, extra, children, className = '', bodyClassName = '' }) => (
  <div className={`card !p-0 !mb-0 flex flex-col ${className}`}>
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-3 border-0 border-b border-solid border-gray-200 dark:border-border-secondary">
      <span className="inline-flex items-center gap-2 min-w-0 text-base font-medium text-text">
        {Icon && <Icon style={{ color: iconColor }} className="shrink-0" />}
        <span className="truncate">{title}</span>
        {badge}
      </span>
      {extra && <div className="flex items-center gap-2 shrink-0">{extra}</div>}
    </div>
    <div className={`p-4 flex-1 min-w-0 ${bodyClassName}`}>{children}</div>
  </div>
);

// LMS count bubble (antd <Badge count>) used next to card titles.
const CountBadge = ({ count, color }) => (
  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-white text-xs font-normal leading-none" style={{ background: color }}>
    {count}
  </span>
);

const ChartCard = ({ icon: Icon, title, subtitle, children, iconColor = 'var(--primary-color)', controls, loading, onExpand }) => (
  <DashCard
    icon={Icon}
    iconColor={iconColor}
    title={title}
    extra={
      <>
        {loading && <LoadingSpinner size="sm" />}
        {onExpand && (
          <button type="button" className="btn btn-outline btn-sm" onClick={onExpand} title="Expand & export">
            <FaDownload /> Export
          </button>
        )}
      </>
    }
  >
    {(subtitle || controls) && (
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        {subtitle ? <span className="text-xs text-gray-500 dark:text-text-secondary">{subtitle}</span> : <span />}
        {controls}
      </div>
    )}
    {children}
  </DashCard>
);

// Chart detail modal — reused for every Charts & Graphs card. Renders the
// same chart larger (via Modal's own maximize control for full-screen),
// and exports either the underlying data (Excel) or a branded PDF/print
// snapshot (image of the chart + a data table, through the same
// header/footer print template used across the app).
const ChartExportModal = ({ open, onClose, title, subtitle, renderChart, columns, rows, fileBaseName, reportSettings }) => {
  const captureRef = useRef(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  if (!open) return null;

  const handleExportExcel = () => {
    if (!rows?.length) {
      toast.info('No data to export');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows.map((row) => {
      const out = {};
      columns.forEach((c) => { out[c.label] = row[c.key] ?? ''; });
      return out;
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
    XLSX.writeFile(wb, `${fileBaseName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportPdf = async () => {
    if (!captureRef.current) return;
    setExportingPdf(true);
    try {
      const bg = getComputedStyle(document.documentElement).getPropertyValue('--card-bg').trim() || '#ffffff';
      const canvas = await html2canvas(captureRef.current, { backgroundColor: bg, scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const tableHTML = rows?.length
        ? `<table><thead><tr>${columns.map((c) => `<th>${c.label}</th>`).join('')}</tr></thead><tbody>${rows.map((r) => `<tr>${columns.map((c) => `<td>${r[c.key] ?? ''}</td>`).join('')}</tr>`).join('')}</tbody></table>`
        : '';
      openPrintPreview({
      reportKey: 'dashboard',
        contentHTML: `<div style="text-align:center;margin-bottom:20px;"><img src="${imgData}" style="max-width:100%;" /></div>${tableHTML}`,
        title,
        documentName: title,
        clientName: reportSettings?.report_client_name || 'Client Name',
        clientAddress: unitReportAddress(reportSettings) || 'Client Address',
        clientLogoUrl: reportSettings?.report_client_logo || '',
        primeCleanLogoUrl: reportSettings?.report_primeclean_logo || '',
        revisionNo: reportSettings?.report_document_rev_no || '01',
        formatNo: reportSettings?.report_format_no_dashboard || '',
        unitName: getActivePrintUnitName()
      });
    } catch (error) {
      console.error('Chart PDF export failed:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={title}
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Back to Dashboard</Button>
          <Button variant="outline" onClick={handleExportExcel}>
            <FaFileExcel style={{ marginRight: 6 }} /> Excel
          </Button>
          <Button variant="outline" onClick={handleExportPdf} disabled={exportingPdf}>
            <FaFilePdf style={{ marginRight: 6 }} /> {exportingPdf ? 'Exporting…' : 'PDF / Print'}
          </Button>
        </>
      }
    >
      {subtitle && <p style={{ margin: '0 0 1rem', color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}>{subtitle}</p>}
      <div ref={captureRef} style={{ background: 'var(--card-bg)', padding: '1rem' }}>
        {renderChart(420)}
      </div>
    </Modal>
  );
};

const LIST_COLUMN_BLOCKLIST = /password|signature|hash|token/i;

// Column most likely to be a useful dropdown filter for a drill-down list —
// first match wins.
const FILTER_COLUMN_CANDIDATES = ['status', 'result_status', 'workflow_stage', 'type', 'study_type', 'role', 'department', 'is_completed', 'is_active'];

const humanizeKey = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatCellValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d.toLocaleString();
  }
  return String(value);
};

// Detail/export modal for any tabular data on the Dashboard — the two
// already-loaded lists (Pending Approvals, Recent Activities) as well as the
// lazily-fetched record list behind each stat tile. Columns are taken from
// the `columns` prop when the shape is already known, otherwise derived from
// the first row's keys (the stat-tile drill-downs come back as `SELECT *`).
const DataListModal = ({ open, onClose, title, subtitle, rows, loading, error, columns: columnsProp, onViewFullPage, reportSettings, fileBaseName }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  if (!open) return null;

  const columns = columnsProp || (rows?.length
    ? Object.keys(rows[0]).filter((k) => !LIST_COLUMN_BLOCKLIST.test(k)).map((k) => ({ key: k, label: humanizeKey(k) }))
    : []);

  const filterColumn = rows?.length ? FILTER_COLUMN_CANDIDATES.find((k) => k in rows[0] && columns.some((c) => c.key === k)) : null;
  const filterOptions = filterColumn
    ? Array.from(new Set(rows.map((r) => r[filterColumn]).filter((v) => v !== null && v !== undefined && v !== ''))).sort()
    : [];

  const filteredRows = (rows || []).filter((row) => {
    if (filterColumn && filterValue && String(row[filterColumn]) !== filterValue) return false;
    if (!searchTerm.trim()) return true;
    const term = searchTerm.trim().toLowerCase();
    return columns.some((c) => formatCellValue(row[c.key]).toLowerCase().includes(term));
  });

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedRows = filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleExportExcel = () => {
    if (!filteredRows.length) {
      toast.info('No data to export');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(filteredRows.map((row) => {
      const out = {};
      columns.forEach((c) => { out[c.label] = formatCellValue(row[c.key]); });
      return out;
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
    XLSX.writeFile(wb, `${fileBaseName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportPdf = () => {
    if (!filteredRows.length) {
      toast.info('No data to export');
      return;
    }
    const tableHTML = `<table><thead><tr>${columns.map((c) => `<th>${c.label}</th>`).join('')}</tr></thead><tbody>${filteredRows.map((r) => `<tr>${columns.map((c) => `<td>${formatCellValue(r[c.key])}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
    openPrintPreview({
      reportKey: 'dashboard',
      contentHTML: tableHTML,
      title,
      documentName: title,
      clientName: reportSettings?.report_client_name || 'Client Name',
      clientAddress: unitReportAddress(reportSettings) || 'Client Address',
      clientLogoUrl: reportSettings?.report_client_logo || '',
      primeCleanLogoUrl: reportSettings?.report_primeclean_logo || '',
      revisionNo: reportSettings?.report_document_rev_no || '01',
      formatNo: reportSettings?.report_format_no_dashboard || '',
      unitName: getActivePrintUnitName()
    });
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={title}
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Back to Dashboard</Button>
          {onViewFullPage && (
            <Button variant="ghost" onClick={onViewFullPage}>
              View Full Page <FaArrowRight style={{ marginLeft: 6 }} />
            </Button>
          )}
          <Button variant="outline" onClick={handleExportExcel} disabled={loading || !filteredRows.length}>
            <FaFileExcel style={{ marginRight: 6 }} /> Excel
          </Button>
          <Button variant="outline" onClick={handleExportPdf} disabled={loading || !filteredRows.length}>
            <FaFilePdf style={{ marginRight: 6 }} /> PDF / Print
          </Button>
        </>
      }
    >
      {subtitle && <p style={{ margin: '0 0 1rem', color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}>{subtitle}</p>}
      {!loading && !error && rows?.length > 0 && (
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <SearchBox placeholder="Search this list…" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }} />
          {filterColumn && filterOptions.length > 1 && (
            <FilterSelect label={humanizeKey(filterColumn)} value={filterValue} onChange={(e) => { setFilterValue(e.target.value); setPage(1); }}>
              <option value="">All</option>
              {filterOptions.map((v) => <option key={String(v)} value={v}>{formatCellValue(v)}</option>)}
            </FilterSelect>
          )}
        </div>
      )}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <LoadingSpinner size="md" />
        </div>
      ) : error ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--danger-text)' }}>{error}</div>
      ) : !rows?.length ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No records found</div>
      ) : !filteredRows.length ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No records match your search/filter</div>
      ) : (
        <>
          <div className="table-container">
            <table>
              <thead>
                <tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {paginatedRows.map((row, idx) => (
                  <tr key={row.id ?? idx}>
                    {columns.map((c) => <td key={c.key}>{formatCellValue(row[c.key])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePagination total={filteredRows.length} page={currentPage} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} itemLabel="rows" style={{ marginTop: '10px' }} />
        </>
      )}
    </Modal>
  );
};

const Dashboard = () => {
  const ai = useAIAction();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [myWork, setMyWork] = useState([]);
  const [activities, setActivities] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();

  const [expandedChart, setExpandedChart] = useState(null);
  const [reportSettings, setReportSettings] = useState({});
  const [expandedList, setExpandedList] = useState(null);
  const [listRows, setListRows] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);

  // Each results-derived chart (workflow / outcome / trend) owns its own
  // date-range filter and fetches independently — they don't share one
  // dashboard-wide filter. The two categorical bar charts own their own sort
  // instead, since "date range" doesn't mean anything for a status snapshot.
  const [workflowRange, setWorkflowRange] = useState('30');
  const [workflowOverride, setWorkflowOverride] = useState(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [outcomeRange, setOutcomeRange] = useState('30');
  const [outcomeOverride, setOutcomeOverride] = useState(null);
  const [outcomeLoading, setOutcomeLoading] = useState(false);
  const [trendRange, setTrendRange] = useState('30');
  const [trendOverride, setTrendOverride] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [samplingSort, setSamplingSort] = useState('count_desc');
  const [equipmentSort, setEquipmentSort] = useState('count_desc');

  // Clicking a legend entry toggles that category out of/into its chart —
  // a filter, not a duplicate of clicking the slice/area itself.
  const [hiddenOutcomes, setHiddenOutcomes] = useState(() => new Set());
  const [hiddenTrendSeries, setHiddenTrendSeries] = useState(() => new Set());
  const toggleHidden = (setFn) => (key) => setFn((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleHiddenOutcome = toggleHidden(setHiddenOutcomes);
  const toggleHiddenTrendSeries = toggleHidden(setHiddenTrendSeries);

  useEffect(() => {
    loadDashboardData();
    settingsAPI.getAll().then((res) => setReportSettings(res?.data?.data || {})).catch(() => {});
  }, []);

  const fetchAnalyticsRange = async (range) => (await dashboardAPI.getAnalytics(range))?.data?.data || {};

  // The initial load already covers the default '30' range for every chart.
  // While that load is still in flight `loading` is true, so the mount-time
  // run of each of these effects (dependency arrays always fire once on
  // mount) is a no-op; they only actually fetch once the user changes that
  // chart's own filter.
  useEffect(() => {
    if (loading) return;
    setWorkflowLoading(true);
    fetchAnalyticsRange(workflowRange)
      .then((d) => setWorkflowOverride({ workflowFunnel: d.workflowFunnel }))
      .catch((error) => console.error('Failed to load workflow chart for range', workflowRange, error))
      .finally(() => setWorkflowLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowRange]);

  useEffect(() => {
    if (loading) return;
    setOutcomeLoading(true);
    fetchAnalyticsRange(outcomeRange)
      .then((d) => setOutcomeOverride({ resultsOutcome: d.resultsOutcome }))
      .catch((error) => console.error('Failed to load outcome chart for range', outcomeRange, error))
      .finally(() => setOutcomeLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcomeRange]);

  useEffect(() => {
    if (loading) return;
    setTrendLoading(true);
    fetchAnalyticsRange(trendRange)
      .then((d) => setTrendOverride({ resultsTrend: d.resultsTrend, trendDays: d.trendDays }))
      .catch((error) => console.error('Failed to load trend chart for range', trendRange, error))
      .finally(() => setTrendLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendRange]);

  const RANGE_LABELS = { '7': 'Last 7 Days', '30': 'Last 30 Days', '90': 'Last 90 Days', all: 'All Time' };

  const sortCategorical = (rows, sortKey, countKey, labelKey) => {
    const sorted = [...rows];
    if (sortKey === 'count_asc') sorted.sort((a, b) => a[countKey] - b[countKey]);
    else if (sortKey === 'name_asc') sorted.sort((a, b) => String(a[labelKey]).localeCompare(String(b[labelKey])));
    else sorted.sort((a, b) => b[countKey] - a[countKey]); // count_desc (default)
    return sorted;
  };

  // Pending Approvals / Recent Activities — data's already loaded, just show it.
  const openFixedList = (key, title, rows, columns) => {
    setExpandedList({ key, title, columns });
    setListRows(rows);
    setListLoading(false);
    setListError(null);
  };

  // Stat tiles — data isn't loaded yet, fetch the records behind that tile's count.
  const openStatList = async (statKey, title, link, filter) => {
    setExpandedList({ key: statKey, title, link, filter });
    setListRows(null);
    setListLoading(true);
    setListError(null);
    try {
      const res = await dashboardAPI.getStatRecords(statKey);
      setListRows(res?.data?.data || []);
    } catch (error) {
      console.error('Failed to load stat records:', error);
      setListError('Failed to load records');
    } finally {
      setListLoading(false);
    }
  };

  // Chart segments (bar / pie slice / trend point) — click drills into the
  // records behind that segment, same list+filter+export experience as the
  // stat tiles above.
  const openAnalyticsList = async (chart, key, title, link, filter) => {
    setExpandedList({ key: `${chart}:${key}`, title, link, filter });
    setListRows(null);
    setListLoading(true);
    setListError(null);
    try {
      const res = await dashboardAPI.getAnalyticsRecords(chart, key);
      setListRows(res?.data?.data || []);
    } catch (error) {
      console.error('Failed to load analytics records:', error);
      setListError('Failed to load records');
    } finally {
      setListLoading(false);
    }
  };

  const normalizeStats = (rawStats = {}) => {
    const normalized = { ...(rawStats || {}) };

    normalized.equipment = Number(normalized.equipment ?? normalized.totalEquipment ?? 0);
    normalized.totalEquipment = Number(normalized.totalEquipment ?? normalized.equipment ?? 0);
    normalized.products = Number(normalized.products ?? 0);
    normalized.apis = Number(normalized.apis ?? 0);
    normalized.samplingPlans = Number(normalized.samplingPlans ?? 0);
    normalized.approvedResults = Number(normalized.approvedResults ?? 0);
    normalized.completedTrainings = Number(normalized.completedTrainings ?? 0);
    normalized.activeUsers = Number(normalized.activeUsers ?? 0);
    normalized.validationsPending = Number(normalized.validationsPending ?? normalized.pendingResults ?? 0);
    normalized.pendingResults = Number(normalized.pendingResults ?? normalized.validationsPending ?? 0);

    return normalized;
  };

  const loadDashboardData = async () => {
    try {
      const [statsRes, approvalsRes, myWorkRes, activitiesRes, analyticsRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getPendingApprovals(),
        dashboardAPI.getMyWork(),
        dashboardAPI.getRecentActivities(),
        dashboardAPI.getAnalytics()
      ]);

      const normalizedStats = normalizeStats(statsRes.data.data || {});
      setStats(normalizedStats);
      setPendingApprovals(approvalsRes.data.data || []);
      setMyWork(myWorkRes.data.data || []);
      setActivities(activitiesRes.data.data || []);
      setAnalytics(analyticsRes.data.data || {});
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setStats({
        apis: 0,
        products: 0,
        samplingPlans: 0,
        approvedResults: 0,
        completedTrainings: 0,
        totalEquipment: 0,
        activeUsers: 0,
        validationsPending: 0,
        equipment: 0,
        pendingResults: 0
      });
      setPendingApprovals([]);
      setMyWork([]);
      setActivities([]);
      setAnalytics(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
  };

  // Pending Approvals now always route to the record's own module page
  // (Equipment Master / Product Master / Equipment Trains) so an approver
  // reviews the actual record — and has Approve/Return/Reject there — instead
  // of blind-signing an approval straight from the dashboard tile.
  const reviewPendingApproval = (approval) => {
    if (approval.type === 'product_approval' || approval.type === 'product' || approval.type === 'product_deletion') {
      navigate('/product-master', { state: { openViewProductId: approval.id } });
    } else if (approval.type === 'equipment') {
      navigate('/equipment-master', { state: { openViewEquipmentId: approval.id } });
    } else if (approval.type === 'equipment_train') {
      navigate('/equipment-trains', { state: { openViewTrainId: approval.id } });
    } else if (approval.type === 'results') {
      navigate('/results', { state: { openViewResultId: approval.id } });
    } else {
      navigate('/dashboard');
    }
  };

  // My Work items don't yet support deep-linking to the exact record (most
  // modules only expose that via the navigate-state trick reviewPendingApproval
  // uses for a handful of types) — this opens the module's list page.
  const openMyWorkItem = (item) => { if (item.link) navigate(item.link); };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  // ── Chart data derivation — each results-derived chart reads its own
  // override (set once its own filter has been changed) and falls back to
  // the shared initial-load data until then. ─────────────────────────────────
  const workflowFunnel = useMemo(() => {
    const source = workflowOverride?.workflowFunnel ?? analytics?.workflowFunnel;
    const byStage = {};
    (source || []).forEach((r) => { byStage[r.stage] = r.count; });
    return FUNNEL_STAGES.map((s) => ({ stage: s.key, label: s.label, count: byStage[s.key] || 0 }));
  }, [analytics, workflowOverride]);
  const funnelTotal = workflowFunnel.reduce((sum, s) => sum + s.count, 0);

  const resultsOutcome = useMemo(() => {
    const source = outcomeOverride?.resultsOutcome ?? analytics?.resultsOutcome;
    return (source || []).filter((r) => r.count > 0);
  }, [analytics, outcomeOverride]);
  const outcomeTotal = resultsOutcome.reduce((sum, r) => sum + r.count, 0);

  const resultsTrend = useMemo(() => {
    const source = trendOverride?.resultsTrend ?? analytics?.resultsTrend;
    const byDate = {};
    (source || []).forEach((r) => { byDate[r.date] = r; });
    const days = [];
    const trendDays = trendOverride?.trendDays ?? analytics?.trendDays ?? 30;
    for (let i = trendDays - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const existing = byDate[key];
      days.push({
        date: key,
        label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        pass: existing?.pass || 0,
        fail: existing?.fail || 0,
        pending: existing?.pending || 0
      });
    }
    return days;
  }, [analytics, trendOverride]);
  const trendTotal = resultsTrend.reduce((sum, d) => sum + d.pass + d.fail + d.pending, 0);

  const samplingPlansByType = useMemo(() => sortCategorical(analytics?.samplingPlansByType || [], samplingSort, 'count', 'type'), [analytics, samplingSort]);
  const equipmentStatus = useMemo(() => sortCategorical(analytics?.equipmentStatus || [], equipmentSort, 'count', 'status'), [analytics, equipmentSort]);
  const workflowRangeLabel = RANGE_LABELS[workflowRange] || 'Last 30 Days';
  const outcomeRangeLabel = RANGE_LABELS[outcomeRange] || 'Last 30 Days';
  const trendRangeLabel = RANGE_LABELS[trendRange] || 'Last 30 Days';
  const passRate = outcomeTotal ? Math.round(((resultsOutcome.find((r) => r.status === 'PASS')?.count || 0) / outcomeTotal) * 100) : 0;
  const equipmentTotalCount = equipmentStatus.reduce((sum, r) => sum + r.count, 0);
  const equipmentApprovedRate = equipmentTotalCount ? Math.round(((equipmentStatus.find((r) => r.status === 'Approved')?.count || 0) / equipmentTotalCount) * 100) : 0;
  const trainingCompliance = analytics?.trainingCompliance || { completed: 0, total: 0, percent: 0 };
  const gaugeColor = trainingCompliance.percent >= 90 ? 'var(--success-text)' : trainingCompliance.percent >= 70 ? 'var(--warning-text)' : 'var(--danger-text)';

  // ── Chart renderers, parameterized by height so the same chart can be
  // reused both in its compact card and (larger) inside the expand/export
  // modal — keeps the two views from drifting out of sync. ─────────────────
  const renderWorkflowFunnelChart = (height) => (
    funnelTotal === 0 ? (
      <ChartEmptyState icon={FaTasks} message="No results logged yet" />
    ) : (
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          <BarChart data={workflowFunnel} layout="vertical" margin={{ top: 4, right: 28, left: 4, bottom: 4 }} barCategoryGap="22%">
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
            <YAxis type="category" dataKey="label" width={92} tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
            <Tooltip
              cursor={{ fill: 'var(--card-hover-bg)' }}
              content={<ChartTooltip formatter={(p) => `${p.payload.count} result${p.payload.count === 1 ? '' : 's'} (${funnelTotal ? Math.round((p.payload.count / funnelTotal) * 100) : 0}%)`} />}
            />
            <Bar
              dataKey="count"
              radius={[0, 6, 6, 0]}
              maxBarSize={26}
              isAnimationActive={false}
              onClick={(entry) => entry.count > 0 && openAnalyticsList('workflow', entry.stage, `Sample & Test Workflow — ${entry.label}`, '/results', { filterStage: entry.stage })}
            >
              {workflowFunnel.map((entry, idx) => (
                <Cell key={entry.stage} fill={funnelFill(idx, workflowFunnel.length)} style={{ cursor: entry.count > 0 ? 'pointer' : 'default' }} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    )
  );

  // Legend items double as filters here: clicking "FAIL" drops it out of the
  // ring (and the center total) without losing it from the legend, so it can
  // be clicked again to bring it back — the legend stays built off the full
  // `resultsOutcome` list even though the Pie itself only ever sees what's
  // still visible.
  const visibleOutcome = resultsOutcome.filter((r) => !hiddenOutcomes.has(r.status));
  const visibleOutcomeTotal = visibleOutcome.reduce((sum, r) => sum + r.count, 0);
  const renderResultsOutcomeChart = (height) => (
    outcomeTotal === 0 ? (
      <ChartEmptyState icon={FaChartPie} message="No results yet" />
    ) : (
      <div style={{ position: 'relative', width: '100%', height }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={visibleOutcome}
              dataKey="count"
              nameKey="status"
              innerRadius="58%"
              outerRadius="80%"
              paddingAngle={2}
              stroke="var(--card-bg)"
              strokeWidth={2}
              isAnimationActive={false}
              label={({ cx, cy, midAngle, outerRadius: r, percent }) => {
                const RADIAN = Math.PI / 180;
                const radius = r + 16;
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                return (
                  <text x={x} y={y} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11} fontWeight={700} fill="var(--text-secondary)">
                    {`${Math.round(percent * 100)}%`}
                  </text>
                );
              }}
              labelLine={false}
              onClick={(_, index) => {
                const entry = visibleOutcome[index];
                if (entry) openAnalyticsList('outcome', entry.status, `Results Outcome — ${entry.status}`, '/results');
              }}
            >
              {visibleOutcome.map((entry) => (
                <Cell key={entry.status} fill={outcomeColor(entry.status)} style={{ cursor: 'pointer' }} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip formatter={(p) => `${p.name}: ${p.value} (${visibleOutcomeTotal ? Math.round((p.value / visibleOutcomeTotal) * 100) : 0}%)`} />} />
            <Legend
              verticalAlign="bottom"
              iconType="square"
              payload={resultsOutcome.map((r) => ({ value: `${r.status} (${r.count})`, type: 'square', color: outcomeColor(r.status), id: r.status }))}
              formatter={(value, entry) => (
                <span style={{
                  color: hiddenOutcomes.has(entry.id) ? 'var(--text-light)' : 'var(--text-secondary)',
                  fontSize: 'var(--fs-sm)',
                  textDecoration: hiddenOutcomes.has(entry.id) ? 'line-through' : 'none'
                }}>
                  {value}
                </span>
              )}
              onClick={(entry) => toggleHiddenOutcome(entry.id)}
            />
          </PieChart>
        </ResponsiveContainer>
        <div style={{
          position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)',
          textAlign: 'center', pointerEvents: 'none'
        }}>
          <div style={{ fontSize: 'var(--fs-2xl)', fontWeight: 800, color: 'var(--text-primary)' }}>{visibleOutcomeTotal}</div>
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>Total</div>
        </div>
      </div>
    )
  );

  const renderResultsTrendChart = (height) => (
    trendTotal === 0 ? (
      <ChartEmptyState icon={FaChartLine} message="No results logged in the last 30 days" />
    ) : (
      <div style={{ width: '100%', height, cursor: 'pointer' }}>
        <ResponsiveContainer>
          <AreaChart
            data={resultsTrend}
            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
            onClick={(state) => {
              const day = state?.activeLabel && resultsTrend.find((d) => d.label === state.activeLabel);
              if (day && (day.pass + day.fail + day.pending) > 0) {
                openAnalyticsList('trend', day.date, `Results — ${day.label}`, '/results');
              }
            }}
          >
            <defs>
              <linearGradient id="fillPass" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--success-text)" stopOpacity={0.35} />
                <stop offset="95%" stopColor="var(--success-text)" stopOpacity={0.03} />
              </linearGradient>
              <linearGradient id="fillFail" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--danger-text)" stopOpacity={0.35} />
                <stop offset="95%" stopColor="var(--danger-text)" stopOpacity={0.03} />
              </linearGradient>
              <linearGradient id="fillPending" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--warning-text)" stopOpacity={0.35} />
                <stop offset="95%" stopColor="var(--warning-text)" stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
            <XAxis dataKey="label" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
            <YAxis allowDecimals={false} tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
            <Tooltip content={<ChartTooltip formatter={(p) => `${p.name}: ${p.value}`} />} labelFormatter={(label) => label} />
            <Legend
              iconType="square"
              formatter={(value, entry) => (
                <span style={{
                  color: hiddenTrendSeries.has(entry.dataKey) ? 'var(--text-light)' : 'var(--text-secondary)',
                  fontSize: 'var(--fs-sm)',
                  textDecoration: hiddenTrendSeries.has(entry.dataKey) ? 'line-through' : 'none'
                }}>
                  {value}
                </span>
              )}
              onClick={(entry) => toggleHiddenTrendSeries(entry.dataKey)}
            />
            <Area type="monotone" dataKey="pass" name="PASS" stackId="1" stroke="var(--success-text)" strokeWidth={2} fill="url(#fillPass)" isAnimationActive={false} hide={hiddenTrendSeries.has('pass')} />
            <Area type="monotone" dataKey="fail" name="FAIL" stackId="1" stroke="var(--danger-text)" strokeWidth={2} fill="url(#fillFail)" isAnimationActive={false} hide={hiddenTrendSeries.has('fail')} />
            <Area type="monotone" dataKey="pending" name="Pending" stackId="1" stroke="var(--warning-text)" strokeWidth={2} fill="url(#fillPending)" isAnimationActive={false} hide={hiddenTrendSeries.has('pending')} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    )
  );

  const renderSamplingPlansChart = (height) => (
    samplingPlansByType.length === 0 ? (
      <ChartEmptyState icon={FaClipboardList} message="No active sampling plans" />
    ) : (
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          <BarChart data={samplingPlansByType} margin={{ top: 8, right: 12, left: -12, bottom: 8 }} barCategoryGap="30%">
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
            <XAxis dataKey="type" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
            <YAxis allowDecimals={false} tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
            <Tooltip cursor={{ fill: 'var(--card-hover-bg)' }} content={<ChartTooltip formatter={(p) => `${p.payload.type}: ${p.value} plan${p.value === 1 ? '' : 's'}`} />} />
            <Bar
              dataKey="count"
              radius={[6, 6, 0, 0]}
              maxBarSize={40}
              isAnimationActive={false}
              onClick={(entry) => openAnalyticsList('samplingPlans', entry.type, `Sampling Plans — ${entry.type}`, '/sampling-plans', { filterStudyType: entry.type })}
            >
              {samplingPlansByType.map((entry, idx) => <Cell key={entry.type} fill={categoricalColor(idx)} style={{ cursor: 'pointer' }} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    )
  );

  const renderEquipmentStatusChart = (height) => (
    equipmentStatus.length === 0 ? (
      <ChartEmptyState icon={FaCog} message="No equipment records" />
    ) : (
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          <BarChart data={equipmentStatus} layout="vertical" margin={{ top: 8, right: 24, left: 4, bottom: 8 }} barCategoryGap="26%">
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
            <YAxis type="category" dataKey="status" width={110} tick={{ fill: 'var(--text-secondary)', fontSize: 10.5 }} />
            <Tooltip cursor={{ fill: 'var(--card-hover-bg)' }} content={<ChartTooltip formatter={(p) => `${p.payload.status}: ${p.value}`} />} />
            <Bar
              dataKey="count"
              radius={[0, 6, 6, 0]}
              maxBarSize={22}
              isAnimationActive={false}
              onClick={(entry) => openAnalyticsList('equipmentStatus', entry.status, `Equipment — ${entry.status}`, '/equipment-master', { filterStatus: entry.status })}
            >
              {equipmentStatus.map((entry, idx) => <Cell key={entry.status} fill={equipmentColor(entry.status, idx)} style={{ cursor: 'pointer' }} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    )
  );

  const renderTrainingComplianceChart = (height) => (
    trainingCompliance.total === 0 ? (
      <ChartEmptyState icon={FaGraduationCap} message="No training records yet" />
    ) : (
      <div
        style={{ position: 'relative', width: '100%', height, cursor: 'pointer' }}
        onClick={() => openAnalyticsList('trainingCompliance', 'all', 'Training Records', '/training')}
        title="Click to view training records"
      >
        <ResponsiveContainer>
          <RadialBarChart
            innerRadius="72%"
            outerRadius="100%"
            data={[{ name: 'Compliance', value: trainingCompliance.percent, fill: gaugeColor }]}
            startAngle={90}
            endAngle={-270}
          >
            <RadialBar dataKey="value" cornerRadius={8} background={{ fill: 'var(--border-light)' }} isAnimationActive={false} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div style={{
          position: 'absolute', top: '48%', left: '50%', transform: 'translate(-50%, -50%)',
          textAlign: 'center', pointerEvents: 'none'
        }}>
          <div style={{ fontSize: 'var(--fs-2xl)', fontWeight: 800, color: gaugeColor }}>{trainingCompliance.percent}%</div>
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>{trainingCompliance.completed} / {trainingCompliance.total} completed</div>
        </div>
      </div>
    )
  );

  // Config for the expand/export modal, keyed the same as the setExpandedChart calls below.
  const chartExportConfigs = {
    workflow: {
      title: 'Sample & Test Workflow',
      subtitle: `Where every logged result currently sits in the sign-off trail — ${workflowRangeLabel}`,
      renderChart: renderWorkflowFunnelChart,
      columns: [{ key: 'label', label: 'Stage' }, { key: 'count', label: 'Count' }],
      rows: workflowFunnel.map((s) => ({ label: s.label, count: s.count })),
      fileBaseName: 'sample_test_workflow'
    },
    outcome: {
      title: 'Results Outcome',
      subtitle: `PASS / FAIL / Pending mix — ${outcomeRangeLabel}`,
      renderChart: renderResultsOutcomeChart,
      columns: [{ key: 'status', label: 'Status' }, { key: 'count', label: 'Count' }, { key: 'percent', label: 'Percent' }],
      rows: resultsOutcome.map((r) => ({ status: r.status, count: r.count, percent: outcomeTotal ? `${Math.round((r.count / outcomeTotal) * 100)}%` : '0%' })),
      fileBaseName: 'results_outcome'
    },
    trend: {
      title: `Results Trend — ${trendRangeLabel}`,
      subtitle: 'Daily test volume split by outcome',
      renderChart: renderResultsTrendChart,
      columns: [{ key: 'label', label: 'Date' }, { key: 'pass', label: 'PASS' }, { key: 'fail', label: 'FAIL' }, { key: 'pending', label: 'Pending' }],
      rows: resultsTrend.map((d) => ({ label: d.label, pass: d.pass, fail: d.fail, pending: d.pending })),
      fileBaseName: 'results_trend'
    },
    samplingPlans: {
      title: 'Sampling Plans by Type',
      renderChart: renderSamplingPlansChart,
      columns: [{ key: 'type', label: 'Type' }, { key: 'count', label: 'Count' }],
      rows: samplingPlansByType.map((s) => ({ type: s.type, count: s.count })),
      fileBaseName: 'sampling_plans_by_type'
    },
    equipmentStatus: {
      title: 'Equipment Status',
      renderChart: renderEquipmentStatusChart,
      columns: [{ key: 'status', label: 'Status' }, { key: 'count', label: 'Count' }],
      rows: equipmentStatus.map((s) => ({ status: s.status, count: s.count })),
      fileBaseName: 'equipment_status'
    },
    trainingCompliance: {
      title: 'Training Compliance',
      renderChart: renderTrainingComplianceChart,
      columns: [{ key: 'metric', label: 'Metric' }, { key: 'value', label: 'Value' }],
      rows: [
        { metric: 'Completed', value: trainingCompliance.completed },
        { metric: 'Total', value: trainingCompliance.total },
        { metric: 'Compliance %', value: `${trainingCompliance.percent}%` }
      ],
      fileBaseName: 'training_compliance'
    }
  };

  if (loading && !refreshing) {
    return (
      <div className="loading-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const statTiles = [
    { title: 'Active Products', value: stats?.products ?? 0, icon: FaBoxOpen, color: 'success', link: '/product-master', statKey: 'products' },
    { title: 'Active APIs', value: stats?.apis ?? 0, icon: FaFlask, color: 'primary', link: '/api-master', statKey: 'apis' },
    { title: 'Total Equipment', value: stats?.totalEquipment ?? stats?.equipment ?? 0, icon: FaCog, color: 'info', link: '/equipment-master', statKey: 'equipment' },
    { title: 'Sampling Plans', value: stats?.samplingPlans ?? 0, icon: FaClipboardList, color: 'primary', link: '/sampling-plans', statKey: 'samplingPlans' },
    { title: 'Pending Results', value: stats?.validationsPending ?? stats?.pendingResults ?? 0, icon: FaClock, color: 'warning', link: '/results', statKey: 'pendingResults' },
    { title: 'Approved Results', value: stats?.approvedResults ?? 0, icon: FaCheckCircle, color: 'success', link: '/results', statKey: 'approvedResults' },
    { title: 'Training Completed', value: stats?.completedTrainings ?? 0, icon: FaGraduationCap, color: 'primary', link: '/training', statKey: 'completedTrainings' },
    { title: 'Active Users', value: stats?.activeUsers ?? 0, icon: FaUsers, color: 'info', link: '/users', statKey: 'activeUsers' },
  ];

  // Status-breakdown rows for the 30-day snapshot card (LMS "Assignment
  // Status" list pattern: colored dot, label, value, caret).
  const snapshotRows = [
    { label: 'Pass Rate', value: `${passRate}%`, color: '#52c41a' },
    { label: 'Results Logged', value: trendTotal, color: '#1890ff' },
    { label: 'Equipment Approved', value: `${equipmentApprovedRate}%`, color: '#13c2c2' },
    { label: 'Training Compliance', value: `${trainingCompliance.percent}%`, color: trainingCompliance.percent >= 70 ? '#52c41a' : '#f5222d' },
  ];

  const rangeOptions = [{ value: '7', label: 'Last 7 days' }, { value: '30', label: 'Last 30 days' }, { value: '90', label: 'Last 90 days' }, { value: 'all', label: 'All time' }];
  const sortOptions = [{ value: 'count_desc', label: 'High → Low' }, { value: 'count_asc', label: 'Low → High' }, { value: 'name_asc', label: 'A → Z' }];

  const approvalTag = (approval) => (approval.type === 'product_deletion'
    ? <span className="tag bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 border-red-200 dark:border-red-500/30">Deletion</span>
    : <span className="tag bg-yellow-50 dark:bg-yellow-500/10 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-500/30">Pending</span>);

  const activityTagClass = (status) => ({
    success: 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-300 border-green-200 dark:border-green-500/30',
    pending: 'bg-yellow-50 dark:bg-yellow-500/10 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-500/30',
    error: 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 border-red-200 dark:border-red-500/30',
  }[status] || 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30');

  const quickActions = [
    { label: 'Products', icon: FaBoxOpen, to: '/product-master' },
    { label: 'Equipment', icon: FaCog, to: '/equipment-master' },
    { label: 'Sampling Plans', icon: FaClipboardList, to: '/sampling-plans' },
    { label: 'Results', icon: FaCheckCircle, to: '/results' },
    { label: 'Training', icon: FaGraduationCap, to: '/training' },
    { label: 'Worst Case', icon: FaExclamationTriangle, to: '/worst-case' },
  ];

  return (
    <div>
      {/* ── Header (LMS DashboardPage) ─────────────────────────────────────── */}
      <div className="flex justify-between items-start gap-3 flex-wrap mb-4">
        <div>
          <h4 className="page-title">Dashboard</h4>
          <span className="page-subtitle">{getGreeting()} — cleaning validation overview</span>
        </div>
        <button className="btn btn-outline" onClick={handleRefresh} disabled={refreshing}>
          <FaSync className={refreshing ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="lms-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={activeTab === 'overview'} className={`lms-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
          <FaThLarge /> Overview
        </button>
        <button type="button" role="tab" aria-selected={activeTab === 'charts'} className={`lms-tab ${activeTab === 'charts' ? 'active' : ''}`} onClick={() => setActiveTab('charts')}>
          <FaChartLine /> Analytics
        </button>
      </div>

      {activeTab === 'overview' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {statTiles.map((stat, index) => (
              <StatCard
                key={stat.statKey}
                title={stat.title}
                value={stat.value}
                icon={stat.icon}
                color={stat.color}
                delay={index * 40}
                onClick={() => openStatList(stat.statKey, stat.title, stat.link, STAT_TILE_FILTERS[stat.statKey])}
              />
            ))}
          </div>

          {/* My Work — everything currently awaiting THIS user's signature across every
              approval-engine workflow (not the broader plant-wide feed below), oldest first. */}
          <DashCard
            icon={FaTasks}
            iconColor="#722ed1"
            title="My Work"
            badge={<CountBadge count={myWork.length} color={myWork.length ? '#f5222d' : '#52c41a'} />}
            extra={
              <button
                type="button"
                className="btn btn-outline btn-sm"
                title="Expand & export"
                onClick={() => openFixedList('myWork', 'My Work', myWork, [
                  { key: 'name', label: 'Record' },
                  { key: 'label', label: 'Workflow' },
                  { key: 'level_label', label: 'Waiting On' },
                  { key: 'aging_days', label: 'Days Pending' }
                ])}
              >
                <FaExpandAlt /> View all
              </button>
            }
            className="mb-4"
          >
            {myWork.length > 0 ? (
              <div className="table-container">
                <table>
                  <thead>
                    <tr><th>Record</th><th>Workflow</th><th>Waiting On</th><th>Age</th><th style={{ width: 90 }}>Action</th></tr>
                  </thead>
                  <tbody>
                    {myWork.slice(0, 6).map((item) => (
                      <tr key={item.request_id}>
                        <td>
                          <span className="text-primary cursor-pointer font-medium" title="Open this record to review it" onClick={() => openMyWorkItem(item)}>
                            {item.name}
                          </span>
                        </td>
                        <td className="text-gray-500 dark:text-text-secondary">{item.label}</td>
                        <td className="text-gray-500 dark:text-text-secondary">{item.level_label}</td>
                        <td>
                          <span style={{ color: item.aging_days > 7 ? '#f5222d' : item.aging_days > 2 ? '#fa8c16' : undefined }}>
                            {item.aging_days}d
                          </span>
                        </td>
                        <td>
                          <button type="button" className="btn btn-primary btn-sm" title="Open to review" onClick={() => openMyWorkItem(item)}>
                            <FaEye /> Review
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-text-secondary">
                <FaCheckCircle className="text-3xl mb-2" style={{ color: '#52c41a' }} />
                <span className="text-sm">Nothing waiting for your signature</span>
              </div>
            )}
          </DashCard>

          <div className="grid grid-cols-1 lg:grid-cols-[15fr_9fr] gap-4 mb-4">
            {/* Pending Approvals — LMS "My Pending Assignments" card */}
            <DashCard
              icon={FaTasks}
              iconColor="#fa8c16"
              title="Pending Approvals"
              badge={<CountBadge count={pendingApprovals.length} color={pendingApprovals.length ? '#f5222d' : '#52c41a'} />}
              extra={
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  title="Expand & export"
                  onClick={() => openFixedList('pendingApprovals', 'Pending Approvals', pendingApprovals, [
                    { key: 'name', label: 'Name' },
                    { key: 'module', label: 'Module' },
                    { key: 'type', label: 'Type' },
                    { key: 'date', label: 'Date' }
                  ])}
                >
                  <FaExpandAlt /> View all
                </button>
              }
            >
              {pendingApprovals.length > 0 ? (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr><th>Record</th><th>Module</th><th>Status</th><th style={{ width: 90 }}>Action</th></tr>
                    </thead>
                    <tbody>
                      {pendingApprovals.slice(0, 6).map((approval, idx) => (
                        <tr key={approval.id ?? idx}>
                          <td>
                            <span className="text-primary cursor-pointer font-medium" title="Open this record to review it" onClick={() => reviewPendingApproval(approval)}>
                              {approval.name || 'Pending Item'}
                            </span>
                          </td>
                          <td className="text-gray-500 dark:text-text-secondary">{approval.module || approval.type}</td>
                          <td>{approvalTag(approval)}</td>
                          <td>
                            <button type="button" className="btn btn-primary btn-sm" title="View & approve / reject / return" onClick={() => reviewPendingApproval(approval)}>
                              <FaEye /> Review
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-text-secondary">
                  <FaCheckCircle className="text-3xl mb-2" style={{ color: '#52c41a' }} />
                  <span className="text-sm">Nothing waiting for approval</span>
                </div>
              )}
            </DashCard>

            {/* Snapshot — LMS status-breakdown list */}
            <DashCard icon={FaChartBar} iconColor="#1890ff" title="Snapshot — Last 30 Days">
              <div className="flex flex-col">
                {snapshotRows.map((row) => (
                  <div
                    key={row.label}
                    onClick={() => setActiveTab('charts')}
                    className="flex items-center justify-between px-1.5 py-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-white/5"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-[7px] h-[7px] rounded-full" style={{ background: row.color }} />
                      <span className="text-[12.5px] text-text">{row.label}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-bold" style={{ color: row.color }}>{row.value}</span>
                      <FaCaretRight className="text-[9px] text-gray-400" />
                    </div>
                  </div>
                ))}
                <div
                  onClick={() => openStatList('pendingResults', 'Pending Results', '/results')}
                  className="flex items-center justify-between px-1.5 py-2 mt-0.5 rounded-lg cursor-pointer transition-colors border-0 border-t border-solid border-gray-200 dark:border-border-secondary hover:bg-gray-50 dark:hover:bg-white/5"
                >
                  <span className="text-[12.5px] text-gray-400">Pending Approvals</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-bold text-text">{pendingApprovals.length}</span>
                    <FaCaretRight className="text-[9px] text-gray-400" />
                  </div>
                </div>
              </div>
            </DashCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[15fr_9fr] gap-4">
            {/* Recent Activities */}
            <DashCard
              icon={FaFileAlt}
              iconColor="#1890ff"
              title="Recent Activities"
              badge={<CountBadge count={activities.length} color="#1890ff" />}
              extra={
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  title="Expand & export"
                  onClick={() => openFixedList('activities', 'Recent Activities', activities, [
                    { key: 'action', label: 'Action' },
                    { key: 'user', label: 'User' },
                    { key: 'timestamp', label: 'Timestamp' },
                    { key: 'status', label: 'Status' }
                  ])}
                >
                  <FaExpandAlt /> View all
                </button>
              }
            >
              {activities.length > 0 ? (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr><th>Activity</th><th>By</th><th>When</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {activities.slice(0, 8).map((activity, idx) => (
                        <tr key={idx}>
                          <td className="font-medium">{activity.action || 'Activity'}</td>
                          <td className="text-gray-500 dark:text-text-secondary">{activity.user || 'System'}</td>
                          <td className="text-xs text-gray-500 dark:text-text-secondary whitespace-nowrap">{activity.timestamp ? new Date(activity.timestamp).toLocaleString() : 'Just now'}</td>
                          <td>{activity.status && <span className={`tag ${activityTagClass(activity.status)}`}>{activity.status}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-text-secondary">
                  <FaBell className="text-3xl mb-2 opacity-40" />
                  <span className="text-sm">No recent activities</span>
                </div>
              )}
            </DashCard>

            {/* Quick Actions */}
            <DashCard icon={FaArrowRight} iconColor="#1890ff" title="Quick Actions">
              <div className="grid grid-cols-2 gap-2">
                {quickActions.map((qa) => (
                  <button key={qa.to} type="button" className="btn btn-outline !justify-start" onClick={() => navigate(qa.to)}>
                    <qa.icon className="text-primary" /> {qa.label}
                  </button>
                ))}
                <button
                  type="button"
                  className="btn btn-outline !justify-start col-span-2"
                  data-module="ai_assistant"
                  onClick={() => ai.run('AI Data Review — Overall', () => aiAPI.reviewData('dashboard'))}
                >
                  <FaRobot className="text-primary" /> AI Data Review
                </button>
              </div>
            </DashCard>
          </div>
        </>
      )}

      {activeTab === 'charts' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
            <ChartCard
              icon={FaChartPie} title="Results Outcome"
              subtitle="PASS / FAIL / Pending mix"
              iconColor="var(--primary-color)" onExpand={() => setExpandedChart('outcome')}
              loading={outcomeLoading}
              controls={<CardControl title="Date range" value={outcomeRange} onChange={(e) => setOutcomeRange(e.target.value)} options={rangeOptions} />}
            >
              {renderResultsOutcomeChart(280)}
            </ChartCard>

            <ChartCard
              icon={FaTasks} title="Sample & Test Workflow"
              subtitle="Where each result sits in the sign-off trail"
              iconColor="var(--primary-color)" onExpand={() => setExpandedChart('workflow')}
              loading={workflowLoading}
              controls={<CardControl title="Date range" value={workflowRange} onChange={(e) => setWorkflowRange(e.target.value)} options={rangeOptions} />}
            >
              {renderWorkflowFunnelChart(280)}
            </ChartCard>

            <ChartCard icon={FaTachometerAlt} title="Training Compliance" iconColor={gaugeColor} onExpand={() => setExpandedChart('trainingCompliance')}>
              {renderTrainingComplianceChart(280)}
            </ChartCard>
          </div>

          <div className="mb-4">
            <ChartCard
              icon={FaChartLine} title={`Results Trend — ${trendRangeLabel}`}
              subtitle="Daily test volume split by outcome"
              iconColor="var(--primary-color)" onExpand={() => setExpandedChart('trend')}
              loading={trendLoading}
              controls={<CardControl title="Date range" value={trendRange} onChange={(e) => setTrendRange(e.target.value)} options={rangeOptions} />}
            >
              {renderResultsTrendChart(280)}
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ChartCard
              icon={FaChartBar} title="Sampling Plans by Type" iconColor="var(--primary-color)"
              onExpand={() => setExpandedChart('samplingPlans')}
              controls={<CardControl title="Sort" value={samplingSort} onChange={(e) => setSamplingSort(e.target.value)} options={sortOptions} />}
            >
              {renderSamplingPlansChart(260)}
            </ChartCard>

            <ChartCard
              icon={FaCog} title="Equipment Status" iconColor="var(--primary-color)"
              onExpand={() => setExpandedChart('equipmentStatus')}
              controls={<CardControl title="Sort" value={equipmentSort} onChange={(e) => setEquipmentSort(e.target.value)} options={sortOptions} />}
            >
              {renderEquipmentStatusChart(260)}
            </ChartCard>
          </div>
        </>
      )}

      {expandedChart && (
        <ChartExportModal
          open={!!expandedChart}
          onClose={() => setExpandedChart(null)}
          reportSettings={reportSettings}
          {...chartExportConfigs[expandedChart]}
        />
      )}

      {expandedList && (
        <DataListModal
          open={!!expandedList}
          onClose={() => setExpandedList(null)}
          title={expandedList.title}
          columns={expandedList.columns}
          rows={listRows}
          loading={listLoading}
          error={listError}
          reportSettings={reportSettings}
          fileBaseName={expandedList.key}
          onViewFullPage={expandedList.link ? () => { navigate(expandedList.link, expandedList.filter ? { state: expandedList.filter } : undefined); setExpandedList(null); } : null}
        />
      )}

      <AIResultPanel
        isOpen={ai.isOpen}
        onClose={ai.close}
        title={ai.title}
        loading={ai.loading}
        error={ai.error}
        content={ai.content}
        exportFileName="dashboard-ai-data-review.pdf"
      />
    </div>
  );
};

export default Dashboard;
